import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useCoursedeeAccess } from '../hooks/useCoursedeeAccess';
import { Loader2, Lock, ShoppingBag } from 'lucide-react';

interface CoursedeeAccessGuardProps {
  children: React.ReactNode;
  requiredVersion: 'v1' | 'v2';
}

const CoursedeeAccessGuard: React.FC<CoursedeeAccessGuardProps> = ({ children, requiredVersion }) => {
  const { user, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  const { loading: accessLoading, hasAccess, error } = useCoursedeeAccess(requiredVersion);
  // เมื่อได้สิทธิ์แล้วครั้งหนึ่ง = ติดหนึบ (จำใน localStorage ต่อ user) — กันโดนเด้งออกตอนเช็คซ้ำหลัง idle
  // หรือ race ตอนรีเฟรช (token refresh/แข่งกับ auth) · คอร์สดีเป็นแพ็คเก็จตลอดชีพ ซื้อแล้วมีสิทธิ์ถาวร
  const grantedRef = useRef(false);
  const grantedKey = user ? `coursedee_access_${requiredVersion}_${user.uid}` : '';
  if (grantedKey && localStorage.getItem(grantedKey) === '1') grantedRef.current = true;
  if (hasAccess && grantedKey) {
    grantedRef.current = true;
    try { localStorage.setItem(grantedKey, '1'); } catch { /* ignore */ }
  }

  useEffect(() => {
    console.log('[CoursedeeAccessGuard] State:', {
      authLoading,
      accessLoading,
      hasUser: !!user,
      hasAccess,
      requiredVersion
    });

    // CRITICAL: Wait for auth loading to finish first
    if (authLoading) {
      console.log('[CoursedeeAccessGuard] Auth still loading, waiting...');
      return;
    }

    // If not logged in after auth loading is done, redirect to login
    if (!user) {
      console.log('[CoursedeeAccessGuard] No user after auth loaded, redirecting to login');
      navigate('/login', { state: { from: `/${requiredVersion}` } });
      return;
    }

    // หมายเหตุ: เอากำแพง "ยืนยันอีเมล" ออกแล้ว — สมัคร/เข้าด้วย email ธรรมดา
    // เข้าถึงได้เลย ประตูเดียวที่เหลือคือ "ซื้อแพ็คเก็จหรือยัง" (hasAccess) ด้านล่าง

    // Wait for access loading to finish before checking access
    if (accessLoading) {
      console.log('[CoursedeeAccessGuard] Access still loading, waiting...');
      return;
    }

    // IMPORTANT: Only redirect if loading is completely done AND no access
    // This prevents redirect during the brief moment when cache is being validated
    if (!accessLoading && !hasAccess && !grantedRef.current) {
      console.log('[CoursedeeAccessGuard] No access after loading complete, redirecting to dashboard');
      navigate('/coursedee/dashboard', {
        state: { 
          message: `คุณยังไม่มีสิทธิ์เข้าถึง AIForYou ${requiredVersion.toUpperCase()}`,
          from: `/${requiredVersion}`
        } 
      });
      return;
    }

    if (hasAccess) {
      console.log('[CoursedeeAccessGuard] Access granted! Rendering children');
    }
  }, [user, authLoading, accessLoading, hasAccess, navigate, requiredVersion]);

  // Show loading screen (wait for both auth and access check) — ข้ามถ้าได้สิทธิ์แล้ว (กันจอโหลดกระพริบตอนเช็คซ้ำ)
  if ((authLoading || accessLoading) && !grantedRef.current) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600 font-medium">กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    );
  }

  // Show error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/coursedee/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            กลับไปหน้า Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show no access screen (only after loading is complete) — ข้ามถ้าเคยได้สิทธิ์แล้ว
  if (!authLoading && !accessLoading && !hasAccess && !grantedRef.current) {
    console.log('[CoursedeeAccessGuard] Rendering no access screen');
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่มีสิทธิ์เข้าถึง</h2>
          <p className="text-gray-600 mb-6">
            คุณยังไม่มีสิทธิ์เข้าถึง AIForYou {requiredVersion.toUpperCase()}
            <br />
            กรุณาซื้อแพ็คเก็จเพื่อเริ่มใช้งาน
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-5 h-5" />
              ซื้อแพ็คเก็จ
            </button>
            <button
              onClick={() => navigate('/coursedee/dashboard')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
            >
              กลับไปหน้า Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User has access, render children
  return <>{children}</>;
};

export default CoursedeeAccessGuard;
